import * as THREE from 'three';
import type { Item } from '../../core/hp/types';

export interface ItemAppearanceOptions { reducedMotion?: () => boolean; random?: () => number }
interface Appearance {
  node: THREE.Object3D; parent: THREE.Object3D | null; started: number;
  position: THREE.Vector3; rotation: THREE.Quaternion; scale: THREE.Vector3;
  shadows: Map<THREE.Mesh, boolean>; posed: boolean; landed: boolean;
  floor: THREE.Vector3; radius: number;
}
interface Dust {
  points: THREE.Points<THREE.BufferGeometry, THREE.ShaderMaterial>; started: number;
  origins: Float32Array; velocities: Float32Array; lives: Float32Array;
}

/**
 * Lite PlanSceneView.animateAppearance / puffDust: .6 m drop, squash, rebound and landing dust.
 * Presentation only; Viewer restores these poses before geometry, picking and 2D work.
 */
export class ItemAppearance {
  private items = new Map<string, Appearance>();
  private dust = new Map<string, Dust>();
  private disposed = false;
  constructor(private effects: THREE.Group, private options: ItemAppearanceOptions = {}) {}
  get active(): boolean { return this.items.size > 0 || this.dust.size > 0; }

  begin(id: string, node: THREE.Object3D, item: Item, nowMs: number): void {
    if (this.disposed || this.reducedMotion() || !node.parent || this.items.has(id)) return;
    this.removeDust(id);
    this.items.set(id, { ...this.capture(node), started: nowMs, landed: false,
      floor: new THREE.Vector3(item.position.x, .02, item.position.z), radius: Math.max(item.size.w, item.size.d) / 2 });
    this.update(nowMs);
  }

  /** Called after canonical placement; swapping a placeholder never restarts the clock. */
  replace(id: string, node: THREE.Object3D, nowMs: number, deferUpdate = false): void {
    const current = this.items.get(id);
    if (!current) return;
    this.restoreOne(current);
    Object.assign(current, this.capture(node));
    // Reconciliation batches recapture every canonical pose before updating any of them.
    if (!deferUpdate) this.update(nowMs);
  }

  restore(): void { for (const current of this.items.values()) this.restoreOne(current); }

  update(nowMs: number): void {
    if (this.disposed) return;
    if (this.reducedMotion()) { this.settle(); return; }
    for (const [id, current] of this.items) {
      if (!current.node.parent || current.node.parent !== current.parent) { this.settle(id); continue; }
      this.restoreOne(current);
      const elapsed = Math.max(0, nowMs - current.started);
      if (elapsed >= 240 && !current.landed) {
        current.landed = true;
        // A hidden/stalled frame must not emit a delayed cloud long after landing.
        if (elapsed < 1840) this.makeDust(id, current, current.started + 240);
      }
      if (elapsed >= 460) { this.items.delete(id); continue; }
      if (elapsed < 240) {
        current.node.position.y += .6 * (1 - ease(elapsed / 240, true));
        for (const mesh of current.shadows.keys()) mesh.castShadow = false;
      } else {
        const amount = elapsed < 310 ? ease((elapsed - 240) / 70, false)
          : elapsed < 320 ? 1 : 1 - ease((elapsed - 320) / 140, false);
        current.node.scale.multiply(new THREE.Vector3(1 + .05 * amount, 1 - .08 * amount, 1 + .05 * amount));
      }
      current.posed = true;
      current.node.updateMatrixWorld(true);
    }
    for (const [id, dust] of this.dust) {
      const age = Math.max(0, nowMs - dust.started) / 1000;
      if (age >= 1.6) { this.removeDust(id); continue; }
      const positions = dust.points.geometry.getAttribute('position') as THREE.BufferAttribute;
      const alpha = dust.points.geometry.getAttribute('particleAlpha') as THREE.BufferAttribute;
      const distance = (1 - Math.exp(-3 * age)) / 3;
      for (let i = 0; i < alpha.count; i++) {
        positions.setXYZ(i, dust.origins[i * 3] + dust.velocities[i * 3] * distance,
          dust.origins[i * 3 + 1] + dust.velocities[i * 3 + 1] * distance,
          dust.origins[i * 3 + 2] + dust.velocities[i * 3 + 2] * distance);
        alpha.setX(i, .85 * Math.max(0, 1 - age / dust.lives[i]));
      }
      positions.needsUpdate = true; alpha.needsUpdate = true;
    }
  }

  settle(id?: string): void {
    const ids = id === undefined ? new Set([...this.items.keys(), ...this.dust.keys()]) : [id];
    for (const key of ids) {
      const current = this.items.get(key);
      if (current) { this.restoreOne(current); this.items.delete(key); }
      this.removeDust(key);
    }
  }
  dispose(): void { this.settle(); this.disposed = true; }

  private capture(node: THREE.Object3D) {
    const shadows = new Map<THREE.Mesh, boolean>();
    node.traverse(object => { if (object instanceof THREE.Mesh) shadows.set(object, object.castShadow); });
    return { node, parent: node.parent, position: node.position.clone(), rotation: node.quaternion.clone(), scale: node.scale.clone(), shadows, posed: false };
  }
  private restoreOne(current: Appearance): void {
    if (!current.posed) return;
    current.node.position.copy(current.position); current.node.quaternion.copy(current.rotation); current.node.scale.copy(current.scale);
    for (const [mesh, cast] of current.shadows) mesh.castShadow = cast;
    current.posed = false; current.node.updateMatrixWorld(true);
  }
  private reducedMotion(): boolean {
    return this.options.reducedMotion?.() ?? (typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches);
  }
  private makeDust(id: string, current: Appearance, started: number): void {
    const random = this.options.random ?? Math.random;
    const count = Math.round(70 + (random() * 2 - 1) * 15);
    const origins = new Float32Array(count * 3), velocities = new Float32Array(count * 3), lives = new Float32Array(count);
    const sizes = new Float32Array(count), alpha = new Float32Array(count).fill(.85);
    const radius = Math.max(current.radius * 1.1, .2) / 2;
    for (let i = 0; i < count; i++) {
      const angle = random() * Math.PI * 2, r = Math.sqrt(random()) * radius;
      origins[i * 3] = Math.cos(angle) * r; origins[i * 3 + 1] = random() * .02; origins[i * 3 + 2] = Math.sin(angle) * r;
      const speed = .9 + (random() * 2 - 1) * .35;
      velocities[i * 3] = Math.cos(angle) * speed * .6; velocities[i * 3 + 1] = speed * .8; velocities[i * 3 + 2] = Math.sin(angle) * speed * .6;
      lives[i] = .9 + (random() * 2 - 1) * .25; sizes[i] = .12 + (random() * 2 - 1) * .05;
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(origins.slice(), 3));
    geometry.setAttribute('particleSize', new THREE.BufferAttribute(sizes, 1));
    geometry.setAttribute('particleAlpha', new THREE.BufferAttribute(alpha, 1));
    const material = new THREE.ShaderMaterial({ transparent: true, depthWrite: false, toneMapped: false,
      uniforms: { pixelScale: { value: 1 }, color: { value: new THREE.Color(.93, .93, .93).convertSRGBToLinear() } },
      vertexShader: `attribute float particleSize; attribute float particleAlpha; uniform float pixelScale; varying float vAlpha;
        void main() { vec4 p = modelViewMatrix * vec4(position, 1.); gl_Position = projectionMatrix * p;
          gl_PointSize = particleSize * pixelScale / max(-p.z, .001); vAlpha = particleAlpha; }`,
      fragmentShader: `uniform vec3 color; varying float vAlpha;
        void main() { float r = length(gl_PointCoord - .5) * 2.; if(r >= 1.) discard;
          gl_FragColor = vec4(color, vAlpha * (1. - smoothstep(.15, 1., r)));
          #include <colorspace_fragment>
        }`,
    });
    const points = new THREE.Points(geometry, material); points.name = `appearance-dust:${id}`;
    points.userData.effectPresentation = true; points.frustumCulled = false; points.position.copy(current.floor);
    const bufferSize = new THREE.Vector2();
    points.onBeforeRender = (renderer, _scene, camera) => {
      material.uniforms.pixelScale.value = renderer.getDrawingBufferSize(bufferSize).y * camera.projectionMatrix.elements[5] / 2;
    };
    this.effects.add(points); this.dust.set(id, { points, started, origins, velocities, lives });
  }
  private removeDust(id: string): void {
    const dust = this.dust.get(id);
    if (!dust) return;
    dust.points.removeFromParent(); dust.points.geometry.dispose(); dust.points.material.dispose(); this.dust.delete(id);
  }
}

// Standard cubic timing curves corresponding to Lite's easeIn/easeOut move stages.
function ease(progress: number, easeIn: boolean): number {
  const x = THREE.MathUtils.clamp(progress, 0, 1), a = easeIn ? .42 : 0, b = easeIn ? 1 : .58;
  if (x === 0 || x === 1) return x;
  let lo = 0, hi = 1;
  for (let i = 0; i < 20; i++) {
    const t = (lo + hi) / 2, u = 1 - t, at = 3 * u * u * t * a + 3 * u * t * t * b + t * t * t;
    if (at < x) lo = t; else hi = t;
  }
  const t = (lo + hi) / 2;
  return 3 * (1 - t) * t * t + t * t * t;
}
